import axios from 'axios';
import { getStoredToken, clearStoredToken } from '../utils/tokenStorage';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth tokens
      clearStoredToken();

      // Only redirect if we're not already on the login page
      // and this isn't the initial auth check (let React handle that)
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      const isOnLoginPage = window.location.pathname === '/login';

      if (!isAuthCheck && !isOnLoginPage) {
        window.location.href = '/login';
      }
    }

    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Insufficient permissions';
      console.warn(`[403 Forbidden] ${error.config?.url}: ${message}`);
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email: string, password: string, subdomain?: string) => {
    // Use v1 endpoint which authenticates against database
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    if (subdomain) {
      formData.append('subdomain', subdomain);
    }
    return api.post('/v1/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
  register: (username: string, email: string, password: string) =>
    api.post('/v1/auth/register', { username, email, password }),
  getMe: () => api.get('/v1/auth/me'),
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  sendInvite: (userId: string) =>
    api.post(`/v1/auth/send-invite/${userId}`),
  setPassword: (token: string, newPassword: string) =>
    api.post('/v1/auth/set-password', { token, new_password: newPassword }),
};

export const filesAPI = {
  list: (projectId?: string) => api.get('/files/', { params: projectId ? { project_id: projectId } : {} }),
  get: (fileId: string) => api.get(`/files/${fileId}`),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (fileId: string) => api.delete(`/files/${fileId}`),
  cancel: (fileId: string) => api.post(`/files/${fileId}/cancel`),
  setPriority: (fileId: string, priority: number) => api.patch(`/files/${fileId}/priority`, { priority }),
  getQueue: () => api.get('/files/queue'),
  move: (fileId: string, projectId: string) => api.put(`/files/${fileId}/move`, { projectId }),
};

export const projectsAPI = {
  list: () => api.get('/projects/'),
  get: (projectId: string) => api.get(`/projects/${projectId}`),
  create: (data: unknown) => api.post('/projects/', data),
  update: (projectId: string, data: unknown) => api.put(`/projects/${projectId}`, data),
  delete: (projectId: string) => api.delete(`/projects/${projectId}`),
  getPreviewMolecules: (projectId: string, limit = 4) =>
    api.get(`/projects/${projectId}/preview-molecules`, { params: { limit } }),
};

export const moleculesAPI = {
  list: (fileId: string, skip = 0, limit = 100) =>
    api.get(`/molecules/${fileId}/molecules`, { params: { skip, limit } }),
  search: (fileId: string, smarts: string) =>
    api.post(`/molecules/${fileId}/search`, { smarts }),
  query: (fileId: string, params: unknown) =>
    api.post(`/files/${fileId}/molecules/query`, params),
};

export const searchAPI = {
  syncSearch: (request: unknown) => api.post('/index/search', request),
};

export const enrichAPI = {
  start: (fileId: string) => api.post('/enrich/patents', { file_id: fileId }),
  getStatus: (taskId: string) => api.get(`/enrich/status/${taskId}`),
};

export const knowledgeAPI = {
  enrichCompound: (smiles: string) =>
    api.get('/enrich/compound', { params: { smiles } }),
  getProtein: (uniprotId: string) => api.get(`/proteins/${uniprotId}`),
  searchProteins: (query: string, limit = 50, offset = 0) =>
    api.get('/proteins/search/', { params: { query, limit, offset } }),
  getInteractions: (proteinId: string, minScore = 700, limit = 100) =>
    api.get(`/interactions/${proteinId}`, { params: { min_score: minScore, limit } }),
  getStructure: (pdbId: string) => api.get(`/structures/${pdbId}`),
  searchStructures: (query: string, limit = 50) =>
    api.get('/structures/search/', { params: { query, limit } }),
  getOrdReaction: (reactionId: string) => api.get(`/ord-reactions/${reactionId}`),
  searchOrdReactions: (smiles: string, searchType = 'product', limit = 50) =>
    api.get('/ord-reactions/search/', { params: { smiles, search_type: searchType, limit } }),
  getStats: () => api.get('/stats'),
};

export const patentsAPI = {
  listClaims: () => api.get('/patents/claims'),
  getClaim: (claimId: string) => api.get(`/patents/claims/${claimId}`),
  createClaim: (data: unknown) => api.post('/patents/claims', data),
  deleteClaim: (claimId: string) => api.delete(`/patents/claims/${claimId}`),
  getEnumeration: (claimId: string, limit = 100, offset = 0) =>
    api.get(`/patents/claims/${claimId}/enumeration`, { params: { limit, offset } }),
  validateScaffold: (scaffoldSmiles: string, rgroupDefinitions: unknown = null) =>
    api.post('/patents/validate-scaffold', {
      scaffold_smiles: scaffoldSmiles,
      rgroup_definitions: rgroupDefinitions,
    }),
  checkCollisions: (moleculeIds: string[], patentClaimId: string, atomThreshold = 6, mcsThreshold = 0.8) =>
    api.post('/patents/check-collisions', {
      molecule_ids: moleculeIds,
      patent_claim_id: patentClaimId,
      atom_threshold: atomThreshold,
      mcs_threshold: mcsThreshold,
    }),
  getCollisionMap: (fileId: string, patentClaimId: string | null = null) =>
    api.get(`/patents/collision-map/${fileId}`, {
      params: patentClaimId ? { patent_claim_id: patentClaimId } : {},
    }),
  generateCollisionMap: (fileId: string, patentClaimIds: string[] | null = null, atomThreshold = 6, mcsThreshold = 0.8) =>
    api.post('/patents/collision-map/generate', {
      file_id: fileId,
      patent_claim_ids: patentClaimIds,
      atom_threshold: atomThreshold,
      mcs_threshold: mcsThreshold,
    }),
  listIsosteres: () => api.get('/patents/isosteres'),
};

// Admin API - User Management
export const usersAPI = {
  list: (params?: { page?: number; per_page?: number; tenant_id?: number; role?: string; is_active?: boolean; search?: string; show_deleted?: boolean }) =>
    api.get('/v1/users', { params }),
  get: (userId: string) => api.get(`/v1/users/${userId}`),
  create: (data: { email: string; username: string; password: string; full_name?: string; role?: string; tenant_id?: number }) =>
    api.post('/v1/users', data),
  update: (userId: string, data: { email?: string; username?: string; full_name?: string; role?: string; is_active?: boolean; tenant_id?: number }) =>
    api.patch(`/v1/users/${userId}`, data),
  delete: (userId: string) => api.delete(`/v1/users/${userId}`),
  changePassword: (userId: string, newPassword: string) =>
    api.post(`/v1/users/${userId}/change-password`, { new_password: newPassword }),
  setRole: (userId: string, role: string) =>
    api.post(`/v1/users/${userId}/set-role`, { role }),
};

// Admin API - Tenant Management
export const tenantsAPI = {
  list: () => api.get('/v1/tenants'),
  get: (tenantId: number) => api.get(`/v1/tenants/${tenantId}`),
  create: (data: { name: string; subdomain?: string; primary_color?: string; secondary_color?: string }) =>
    api.post('/v1/tenants', data),
  update: (tenantId: number, data: { name?: string; subdomain?: string; primary_color?: string; secondary_color?: string; is_active?: boolean; features?: Record<string, boolean> }) =>
    api.patch(`/v1/tenants/${tenantId}`, data),
  delete: (tenantId: number) => api.delete(`/v1/tenants/${tenantId}`),
  getMembers: (tenantId: number) => api.get(`/v1/tenants/${tenantId}/members`),
  harvestColors: (websiteUrl: string) =>
    api.post('/v1/tenants/harvest-colors', { website_url: websiteUrl }),
};

// Admin API - Guest Analytics
export const analyticsAPI = {
  getGuests: () => api.get('/admin/guest-analytics'),
  getGuestTimeline: (userId: string) => api.get(`/admin/guest-analytics/${userId}`),
  getAggregate: () => api.get('/admin/guest-analytics/aggregate'),
};

// Admin API - Audit Log
export const auditAPI = {
  getLog: (params: {
    page?: number;
    per_page?: number;
    severity?: string;
    category?: string;
    date_range?: string;
    search?: string;
    tenant_id?: number;
    user_id?: string;
    action?: string;
    from?: string;
    to?: string;
  }) => api.get('/admin/audit-log', { params }),
  exportCSV: (params: {
    severity?: string;
    category?: string;
    date_range?: string;
    search?: string;
  }) => api.get('/admin/audit-log/export', { params, responseType: 'blob' }),
};

// Project Management / Pipeline API
export const pipelineAPI = {
  getStages: () => api.get('/v1/pm/pipeline-stages'),
  getTherapeuticAreas: () => api.get('/v1/pm/therapeutic-areas'),
  getPortfolioSummary: (params?: { status?: string; therapeutic_area?: string; stage_code?: string }) =>
    api.get('/v1/pm/portfolio/summary', { params }),
  getProjectPM: (projectId: number) => api.get(`/v1/pm/projects/${projectId}`),
  updateProjectPM: (projectId: number, data: unknown) => api.put(`/v1/pm/projects/${projectId}`, data),
  getTeam: (projectId: number) => api.get(`/v1/pm/projects/${projectId}/team`),
  addTeamMember: (projectId: number, data: { user_id: string; role: string; structure_masked: boolean }) =>
    api.post(`/v1/pm/projects/${projectId}/team`, data),
  updateTeamMember: (projectId: number, userId: string, data: { role?: string; structure_masked?: boolean }) =>
    api.put(`/v1/pm/projects/${projectId}/team/${userId}`, data),
  removeTeamMember: (projectId: number, userId: string) =>
    api.delete(`/v1/pm/projects/${projectId}/team/${userId}`),
  getTeamCandidates: (projectId: number, search?: string) =>
    api.get(`/v1/pm/projects/${projectId}/team-candidates`, { params: search ? { search } : undefined }),
  stageTransition: (projectId: number, data: { to_stage_id: number; gate_criteria_met?: Record<string, boolean>; justification?: string }) =>
    api.post(`/v1/pm/projects/${projectId}/stage-transition`, data),
  getStageHistory: (projectId: number) => api.get(`/v1/pm/projects/${projectId}/stage-history`),
  getGateCriteria: (stageId: number) => api.get(`/v1/pm/gate-criteria/${stageId}`),
  getOrgSettings: () => api.get('/v1/pm/org-settings'),
  updateOrgSetting: (key: string, settingVal: Record<string, unknown>) =>
    api.put(`/v1/pm/org-settings/${key}`, { setting_val: settingVal }),
};

// GDPR / Privacy API
export const gdprAPI = {
  acceptConsent: () => api.put('/v2/me/consent'),
  exportData: () => api.get('/v2/me/data-export'),
  correctData: (data: { name?: string; email?: string }) =>
    api.put('/v2/me/data-correction', data),
};

export default api;
